<template>
  <PageTemplate>
    <div class="element-wrapper">
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'billingCoachingSelection' }"
          marginBottom="5"/>

      <h6 class="element-header">
        Rechnungslegung
      </h6>
      <div class="element-box">
        <div class="row">
          <div class="col-6">
            <h5 class="form-header mb-4">
              Ausserordentliche - Rechnungslegung
            </h5>
          </div>
        </div>

        <form @submit.prevent="">
          <div class="element-box">
            <div class="row">
              <div class="col-12">
                <BaseInput
                    v-model="form.title"
                    classFormGroup="mb-0"
                    label="Rechnungstitel"
                    :validation="v$.title"
                    :messages="v$.title.required.$message"/>
              </div>
            </div>

            <div class="row">
              <div class="col-6">
                <BaseInput
                    classFormGroup="mb-0"
                    v-model="form.receiverAddOn"
                    label="Zusatz"/>
              </div>
              <div class="col-6">
                <BaseInput
                    classFormGroup="mb-0"
                    v-model="form.receiverName"
                    label="Empfänger"/>
              </div>
            </div>

            <!-- Receiver Information -->
            <div class="row">
              <div class="col-6">
                <BaseInput
                    classFormGroup="mb-0"
                    v-model="form.receiverCity"
                    label="Stadt"/>
              </div>
              <div class="col-6">
                <BaseInput
                    classFormGroup="mb-0"
                    v-model="form.receiverAddress"
                    label="Straße"/>
              </div>
            </div>

            <div class="row">
              <div class="col-6">
                <BaseInput
                    classFormGroup="mb-0"
                    v-model="form.receiverZip"
                    label="PLZ"/>
              </div>
              <div class="col-6">
                <BaseInput
                    classFormGroup="mb-0"
                    v-model="form.receiverCountry"
                    label="Land"/>
              </div>
            </div>

            <!-- Cost Centers -->
            <div class="row">
              <div class="col-6">
                <div class="row">
                  <div class="col-6">
                    <BaseInput
                        classFormGroup="mb-0"
                        v-model="form.costCenter1"
                        label="KS1"/>
                  </div>
                  <div class="col-6">
                    <BaseInput
                        classFormGroup="mb-0"
                        v-model="form.costCenter2"
                        label="KS2"/>
                  </div>
                </div>
              </div>
            </div>

            <div class="row" v-for="(item, index) in form.items" :key="index">
              <div class="col-lg-6">
                <BaseInput
                    :id="'itemDescription' + index"
                    v-model="item.description"
                    classFormGroup="mb-0"
                    label="Beschreibung"/>
              </div>
              <div class="col-lg-2">
                <BaseNumber
                    :id="'itemQuantity' + index"
                    v-model.number="item.quantity"
                    label="Menge"
                    classFormGroup="mb-0"
                    :decimal-places=2
                    @input="updateItemTotal(index)"/>
              </div>
              <div class="col-lg-2">
                <BaseNumber
                    :id="'itemPrice' + index"
                    :is-currency="true"
                    v-model.number="item.price"
                    label="Preis"
                    classFormGroup="mb-0"
                    :decimal-places=2
                    @input="updateItemTotal(index)"/>
              </div>
              <div class="col-lg-2">
                <BaseNumber
                    :id="'itemTotal' + index"
                    :is-currency="true"
                    v-model.number="item.total"
                    label="Gesamt"
                    readonly="readonly"
                    classFormGroup="mb-0"
                    :decimal-places=2
                    @input="updateItemTotal(index)"/>
              </div>
            </div>

            <div class="row justify-content-end align-items-center">
              <div class="col-2">
                <BaseNumber
                    :is-currency="true"
                    v-model.number="form.billTotal"
                    label="Sum"
                    classFormGroup="mb-0"
                    readonly="readonly"
                    :decimal-places=2 />
              </div>
            </div>

            <div v-if="v$.title.$error" class="text-center">
              <p style="font-size: 16px; color: red">Rechnungstitel ist erforderlich.</p>
            </div>

            <div v-if="form.billTotal === 0" class="text-center">
              <p style="font-size: 16px; color: red">Um eine Rechnung zu erstellen, muss der Betrag größer sein 0.</p>
            </div>

            <div class="row mt-5 d-flex justify-content-center align-items-center text-center">
              <div class="col-auto">
                <button
                    type="submit"
                    @click="createBill"
                    :disabled="form.billTotal === 0"
                    class="btn btn-primary">Rechnung erstellen
                </button>
              </div>
              <div class="col-auto">
                <button type="button" @click="previewBill" class="btn btn-outline-primary">Rechnungsvorschau</button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <ModalConfirm
        v-model="showCreateBillConfirm"
        confirmationQuestion="Sind Sie sicher, dass Sie eine Rechnung erstellen möchten?"
        :header="''"
        :onConfirm="confirmCreateBill"/>
  </PageTemplate>
</template>

<script setup>
import billingService from '@/api/Billing';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import BackButton from '@/components/button/BackButton.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import router from '@/router';
import { downloadFile } from '@/shared/functions';
import { requiredRule } from '@/shared/validators/validationRules';
import useVuelidate from '@vuelidate/core';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const showCreateBillConfirm = ref(false);
const { t }                 = useI18n();

const form = ref({
  title: '',
  receiverAddOn: '',
  receiverName: '',
  receiverAddress: '',
  receiverCity: '',
  receiverZip: '',
  receiverCountry: '',
  costCenter1: '',
  costCenter2: '',
  billTotal: 0,
  items: Array.from({ length: 10 }, () => ({
    description: '',
    quantity: 0,
    price: 0,
    total: 0,
  })),
});

const rules = computed(() => {
  return {
    title: requiredRule(t),
  };
});

const v$ = useVuelidate(rules, form);

const updateItemTotal = (index) => {
  const item = form.value.items[ index ];
  //item.total = item.quantity * item.price;

  //form.value.billTotal = form.value.items.reduce((sum, item) => sum + item.total, 0);
  //form.value.billTotal = parseFloat(
  //    form.value.items.reduce((sum, item) => sum + item.total, 0).toFixed(2)
  //);

  // Precision fix for item total
  item.total = Math.round((item.quantity * item.price) * 100) / 100;

  // Precision fix for bill total
  form.value.billTotal = Math.round(
      form.value.items.reduce((sum, item) => sum + item.total, 0) * 100
  ) / 100;
};


function createBill() {
  showCreateBillConfirm.value = true;
}

async function confirmCreateBill() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    await billingService.createSpecialCaseBill(form.value);

    await router.push({ name: 'billingLatestBills' });
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  }

}

function previewBill() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  downloadFile('/billing/special-case/preview-bill', null, form.value, true, 'POST');
}

onMounted(async () => {
  CloseToast();
});


</script>


<style scoped>
.row {
  margin-bottom: 15px;
}
</style>
